import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import runThemeSwitch from './themeSwitch';

const ThemeContext = createContext();

// Only the theme's own class is swapped: other classes on the body (has-suite-sidenav and the like) belong to the page and must stay.
export const applyThemeClass = (theme) => {
  document.body.classList.remove('light', 'dark');
  document.body.classList.add(theme);
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const persistTimer = useRef(null);

  useEffect(() => {
    applyThemeClass(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    let cancelled = false;
    api.get('/api/users/me/preferences')
      .then((res) => {
        if (cancelled) return;
        const saved = res.data && res.data.theme;
        if (saved === 'light' || saved === 'dark') setTheme(saved);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  const persistTheme = (next) => {
    if (!isAuthenticated) return;
    if (persistTimer.current) window.clearTimeout(persistTimer.current);
    persistTimer.current = window.setTimeout(() => {
      api.put('/api/users/me/preferences', { theme: next }).catch(() => {});
    }, 250);
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    runThemeSwitch(() => {
      setTheme(next);
      // Also set here, not only in the effect above: the new look must be on the page before
      // the switch's snapshot is taken, and that happens as soon as this callback returns.
      applyThemeClass(next);
    });
    persistTheme(next);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
